import { useState, useEffect, useCallback, useMemo } from 'react';
import { getIntents, putIntents, resolveIntent, getCompanies } from './api';

// Editor state for one scope of the intent dictionary — global (companyId null)
// or a company's overrides. Mirrors access-control/useBundles.js.
export default function useIntents() {
    const [companyId, setCompanyId] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [intents, setIntents] = useState([]);        // [{ key, tokens }]
    const [defaults, setDefaults] = useState({});      // { INTENT: [phrase] }
    const [saved, setSaved] = useState({});            // { INTENT: [phrase] } as stored
    const [edited, setEdited] = useState({});          // { INTENT: [phrase] } being edited
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState([]);
    const [tryResult, setTryResult] = useState(null);

    const groupRows = (rows) => {
        const out = {};
        (rows || []).forEach((r) => { (out[r.intent] = out[r.intent] || []).push(r.phrase); });
        return out;
    };

    const load = useCallback(async () => {
        try {
            const res = await getIntents(companyId);
            const d = (res && res.data) || {};
            setIntents(d.intents || []);
            setDefaults(d.defaults || {});
            const grouped = groupRows(d.phrases);
            setSaved(grouped);
            setEdited(grouped);
            setErrors([]);
        } catch {
            setIntents([]); setSaved({}); setEdited({});
        }
    }, [companyId]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        getCompanies()
            .then((res) => {
                const list = Array.isArray(res) ? res : (res && (res.data || res.companies)) || [];
                setCompanies((Array.isArray(list) ? list : []).map((c) => ({ id: c.id, name: c.name })));
            })
            .catch(() => setCompanies([]));
    }, []);

    const setPhrases = useCallback((intent, list) => {
        setEdited((prev) => ({ ...prev, [intent]: [...new Set((list || []).map((s) => String(s).trim()).filter(Boolean))] }));
    }, []);

    const dirty = useMemo(() => {
        const keys = new Set([...Object.keys(saved), ...Object.keys(edited)]);
        for (const k of keys) {
            const a = saved[k] || []; const b = edited[k] || [];
            if (a.length !== b.length || a.some((p) => !b.includes(p))) return true;
        }
        return false;
    }, [saved, edited]);

    const flatten = (map) => Object.entries(map).flatMap(([intent, list]) => (list || []).map((phrase) => ({ intent, phrase })));

    const save = useCallback(async () => {
        setSaving(true);
        setErrors([]);
        try {
            const res = await putIntents(companyId, flatten(edited));
            const grouped = groupRows(res && res.data && res.data.phrases);
            setSaved(grouped);
            setEdited(grouped);
        } catch (err) {
            const payload = err && err.response && err.response.data;
            setErrors((payload && payload.errors) || [(payload && payload.message) || 'Save failed']);
        } finally {
            setSaving(false);
        }
    }, [companyId, edited]);

    const reset = useCallback(() => { setEdited(saved); setErrors([]); }, [saved]);

    // Global scope only: put the code defaults into the editor (still needs Save).
    const restoreDefaults = useCallback(() => {
        if (companyId) return;
        const next = {};
        Object.entries(defaults).forEach(([k, v]) => { next[k] = [...v]; });
        setEdited(next);
    }, [companyId, defaults]);

    const tryText = useCallback(async (text) => {
        if (!text || !text.trim()) { setTryResult(null); return; }
        try {
            const res = await resolveIntent({ text, companyId: companyId || null });
            setTryResult((res && res.data && res.data.text) || null);
        } catch {
            setTryResult(null);
        }
    }, [companyId]);

    return {
        companyId, setCompanyId, companies,
        intents, defaults, edited, setPhrases,
        dirty, saving, errors, save, reset, restoreDefaults,
        tryText, tryResult,
    };
}
